import { API_ENDPOINTS, buildApiUrl } from "../config/api";

export type RegisterPayload = {
  first_name: string;
  last_name: string;
  email: string;
  lgpd_consent: boolean;
};

export type LoginPayload = {
  email: string;
  password: string;
  remember_me: boolean;
};

export type ActivatePayload = {
  token: string;
  new_password: string;
};

export type AuthTokenResponseData = {
  access_token?: string;
  expires_in?: number;
  token_type?: string;
  message?: string;
  mfa_required?: boolean;
};

type ApiResponse<TData = unknown> = {
  status: number;
  message: string;
  data?: TData;
};

async function postJson<TData>(
  endpoint: string,
  payload: Record<string, unknown>
): Promise<ApiResponse<TData>> {
  const response = await fetch(buildApiUrl(endpoint), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const json = (await response.json().catch(() => null)) as ApiResponse<TData> | null;

  if (!response.ok) {
    const errorMessage = json?.data && typeof json.data === "object" && "message" in json.data
      ? String((json.data as { message?: string }).message ?? "")
      : json?.message || "Erro ao processar solicitacao";
    throw new Error(errorMessage);
  }

  if (!json) {
    throw new Error("Resposta invalida da API");
  }

  return json;
}

export function registerUser(payload: RegisterPayload) {
  return postJson<{ message?: string }>(API_ENDPOINTS.register, payload);
}

export function loginUser(payload: LoginPayload) {
  return postJson<AuthTokenResponseData>(API_ENDPOINTS.login, payload);
}

export function activateUser(payload: ActivatePayload) {
  return postJson<AuthTokenResponseData>(API_ENDPOINTS.activate, payload);
}

export async function verifyTwoFactorLogin(payload: {
  code: string;
  mfaToken: string;
}) {
  const response = await fetch(buildApiUrl(API_ENDPOINTS.twoFactorVerify), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${payload.mfaToken}`,
    },
    body: JSON.stringify({ code: payload.code }),
  });

  const json = (await response.json().catch(() => null)) as ApiResponse<AuthTokenResponseData> | null;
  if (!response.ok) {
    const errorMessage = json?.data && typeof json.data === "object" && "message" in json.data
      ? String((json.data as { message?: string }).message ?? "")
      : json?.message || "Erro ao validar o codigo 2FA";
    throw new Error(errorMessage);
  }

  if (!json) {
    throw new Error("Resposta invalida da API");
  }

  return json;
}

export async function logoutUser() {
  const accessToken = localStorage.getItem("access_token");
  if (!accessToken) {
    throw new Error("Token de acesso nao encontrado.");
  }

  await fetch(buildApiUrl(API_ENDPOINTS.logout), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
}
